import type { MockRecord } from '../database.ts';

/** One record-level change. `d` marks a deletion; otherwise `r` is the complete record. */
export interface SyncChange {
  key: string;
  c: string;
  d?: 1;
  r?: MockRecord;
}

export type SyncMessage =
  | { type: 'hello'; epoch: string; digest: Record<string, string>; reply: boolean }
  | { type: 'changes'; epoch: string; items: SyncChange[] }
  | { type: 'ack'; epoch: string; acks: Record<string, string> }
  | { type: 'reset'; epoch: string };

const CLOCK = /^[0-9a-z]{9}\.[0-9a-z]{3}\.[0-9a-z]{1,16}$/;
const TABLE = /^[A-Za-z][A-Za-z0-9]{0,63}$/;
const MAX_ITEMS = 500;
const MAX_DIGEST = 200_000;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const isClock = (value: unknown): value is string => typeof value === 'string' && CLOCK.test(value);
const isEpoch = (value: unknown): value is string => value === '' || isClock(value);

export const isTableName = (value: string): boolean => TABLE.test(value);

export function recordKey(table: string, oid: string): string {
  return `${table}:${oid}`;
}

export function splitKey(key: string): { table: string; oid: string } | undefined {
  const separator = key.indexOf(':');
  if (separator < 1 || key.length > 300) return undefined;
  const table = key.slice(0, separator);
  const oid = key.slice(separator + 1);
  return TABLE.test(table) && oid ? { table, oid } : undefined;
}

function clockMap(value: unknown): Record<string, string> | undefined {
  if (!isObject(value)) return undefined;
  const entries = Object.entries(value);
  if (entries.length > MAX_DIGEST) return undefined;
  const result: Record<string, string> = Object.create(null) as Record<string, string>;
  for (const [key, clock] of entries) {
    if (!isClock(clock)) return undefined;
    result[key] = clock;
  }
  return result;
}

function change(value: unknown): SyncChange | undefined {
  if (!isObject(value) || typeof value.key !== 'string' || !isClock(value.c)) return undefined;
  if (value.d === 1) return { key: value.key, c: value.c, d: 1 };
  return isObject(value.r) && typeof value.r.oid === 'string' ? { key: value.key, c: value.c, r: value.r as MockRecord } : undefined;
}

/** Validates data received from another window; returns undefined for anything that is not a well-formed message. */
export function parseSyncMessage(value: unknown): SyncMessage | undefined {
  if (!isObject(value) || !isEpoch(value.epoch)) return undefined;
  const epoch = value.epoch;
  switch (value.type) {
    case 'hello': {
      const digest = clockMap(value.digest);
      return digest && typeof value.reply === 'boolean' ? { type: 'hello', epoch, digest, reply: value.reply } : undefined;
    }
    case 'changes': {
      if (!Array.isArray(value.items) || value.items.length > MAX_ITEMS) return undefined;
      const items = value.items.map(change);
      return items.every((item) => item) ? { type: 'changes', epoch, items: items as SyncChange[] } : undefined;
    }
    case 'ack': {
      const acks = clockMap(value.acks);
      return acks ? { type: 'ack', epoch, acks } : undefined;
    }
    case 'reset':
      return epoch ? { type: 'reset', epoch } : undefined;
    default:
      return undefined;
  }
}
